import { useState, useCallback, useMemo } from 'react';
import { useSearchParams } from 'react-router';
import { useNavigate } from 'features/cobrand/hooks/useCobrandUtils';
import { useCareProgramsData } from './useCareProgramsData';
import { getAllHealthTopicsSorted } from '../utils/healthTopicUtils';
import { HealthTopic, ProgramSection } from '../types';

export interface UseCareProgramsByTopicOptions {
  topic?: HealthTopic;
}

export interface UseCareProgramsByTopicReturn {
  selectedTopic: HealthTopic;
  groupedProgramsBySections: ProgramSection[];
  handleBack: () => void;
  handleTopicClick: (healthTopic: HealthTopic) => void;
}

/**
 * Fetches care programs and exposes topic selection for the health topics
 * browse page and topic list pages: which topic is selected and its
 * groupedProgramsBySections, syncing to the `topicId` URL param. Pass
 * `{ topic }` when the caller already owns topic selection (e.g. a topics
 * browse page); omit it to let this hook own topic selection itself.
 * Program selection and enrollment concerns (which program is selected, its
 * enrollment status, requesting enrollment, chat handoff) live in
 * useCareProgramRequest, which independently owns its own data fetch too.
 */
export const useCareProgramsByTopic = ({
  topic,
}: UseCareProgramsByTopicOptions = {}): UseCareProgramsByTopicReturn => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const healthTopics = getAllHealthTopicsSorted();
  const { groupedProgramsByTopicMap } = useCareProgramsData();

  // Initialize topic from options, URL, or default to first topic
  const initialTopic = useMemo(() => {
    if (topic) return topic;

    const topicIdFromUrl = searchParams.get('topicId');
    if (topicIdFromUrl) {
      const matchedTopic = healthTopics.find((healthTopic) => healthTopic === topicIdFromUrl);
      if (matchedTopic) return matchedTopic;
    }
    return healthTopics[0];
  }, [topic, searchParams, healthTopics]);

  const [internalSelectedTopic, setInternalSelectedTopic] = useState<HealthTopic>(initialTopic);
  const selectedTopic = topic ?? internalSelectedTopic;

  const groupedProgramsBySections = groupedProgramsByTopicMap[selectedTopic] || [];

  const handleBack = useCallback(() => {
    navigate('/resources');
  }, [navigate]);

  const handleTopicClick = useCallback(
    (healthTopic: HealthTopic) => {
      if (topic) return;

      setInternalSelectedTopic(healthTopic);
      setSearchParams({ topicId: healthTopic });
    },
    [topic, setSearchParams],
  );

  return {
    selectedTopic,
    groupedProgramsBySections,
    handleBack,
    handleTopicClick,
  };
};
